import { t } from "i18next";
import jsPDF from "jspdf";
import autoTable, { CellDef, jsPDFDocument, Styles } from "jspdf-autotable";

import {
  DeliveriesLabels,
  DriverInDeliveriesListItem,
  DriversDeliveriesTotals
} from "../../financial.types";
import {
  getReportBaseStyles,
  getReportGrid,
  REPORT_BORDER_COLOR,
  REPORT_FONT_SIZE,
  REPORT_PRIMARY_COLOR,
  REPORT_TEXT_COLOR
} from "../reportLayout";

interface RenderDriverSummaryProps {
  pdf: jsPDF;
  /** Left-hand label of the block: the driver's name, or "TOTAL" for the closing row. */
  title: string;
  totals: DriversDeliveriesTotals | DriverInDeliveriesListItem;
  labels: DeliveriesLabels;
  font: string;
  startY: number;
  /** The driver's avatar as a data URL, when it could be read. */
  avatar?: string;
}

const AVATAR_SIZE = 28;

const AVATAR_LEFT_PADDING = 12;

/** jsPDF needs the real format of the embedded bytes, not a guess. */
const getImageFormat = (dataUrl: string) =>
  dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")
    ? "JPEG"
    : "PNG";

const format = (value: number) => value.toLocaleString("en-US");

/**
 * The header block that opens every driver's section: the avatar and name on
 * the left, the counts of the period and the money columns on the right. Laid
 * out on the report grid, so it lines up with the deliveries table under it.
 */
export const renderDriverSummary = ({
  pdf,
  title,
  totals,
  labels,
  font,
  startY,
  avatar
}: RenderDriverSummaryProps) => {
  const grid = getReportGrid(pdf);

  const labelStyles: Partial<Styles> = {
    fontStyle: "bold",
    fillColor: REPORT_PRIMARY_COLOR,
    textColor: "#FFFFFF"
  };

  const valueStyles: Partial<Styles> = {
    fontStyle: "bold",
    fontSize: REPORT_FONT_SIZE + 2,
    textColor: REPORT_TEXT_COLOR
  };

  const columnLabels = [
    t("Delivered orders"),
    t("Waiting to be unpacked"),
    t("Unpacked orders"),
    t("Failed orders"),
    t(labels.toCollect),
    t(labels.toPay),
    t(labels.paid),
    t(labels.collected)
  ];

  const values = [
    totals.deliveredOrdersCount,
    totals.waitingToUnpackFailedOrdersCount,
    totals.unpackedFailedOrdersCount,
    totals.failedOrdersCount,
    totals.totalAmountToCollect,
    totals.totalAmountToPay,
    totals.totalAmountPaid,
    totals.totalAmountCollected
  ];

  const titleCell: CellDef = {
    content: title,
    rowSpan: 2,
    styles: {
      fontStyle: "bold",
      fontSize: REPORT_FONT_SIZE + 2,
      halign: "left",
      cellPadding: {
        top: 6,
        right: 8,
        bottom: 6,
        left: avatar ? AVATAR_SIZE + AVATAR_LEFT_PADDING + 8 : 10
      }
    }
  };

  autoTable(pdf, {
    theme: "grid",
    startY,
    margin: grid.margin,
    tableWidth: grid.tableWidth,
    columnStyles: grid.columnStyles,
    body: [
      [
        titleCell,
        ...columnLabels.map((label) => ({ content: label, styles: labelStyles }))
      ],
      values.map((value) => ({ content: format(value), styles: valueStyles }))
    ],
    styles: getReportBaseStyles(font),
    tableLineWidth: 0.5,
    tableLineColor: REPORT_BORDER_COLOR,
    rowPageBreak: "avoid",
    didDrawCell: (data) => {
      if (!avatar || data.column.index !== 0 || data.row.index !== 0) return;

      const centerX = data.cell.x + AVATAR_LEFT_PADDING + AVATAR_SIZE / 2;
      const centerY = data.cell.y + data.cell.height / 2;
      const radius = AVATAR_SIZE / 2;

      // Cover-fit inside a circular clip, so a portrait photo is cropped
      // rather than squashed into a square.
      const { width, height } = pdf.getImageProperties(avatar);
      const scale = AVATAR_SIZE / Math.min(width, height);
      const drawWidth = width * scale;
      const drawHeight = height * scale;

      pdf.saveGraphicsState();
      // `null` adds the circle as a path without painting it, so it can be
      // used as the clipping region.
      pdf.circle(centerX, centerY, radius, null as never);
      pdf.clip();
      pdf.discardPath();

      pdf.addImage(
        avatar,
        getImageFormat(avatar),
        centerX - drawWidth / 2,
        centerY - drawHeight / 2,
        drawWidth,
        drawHeight
      );

      pdf.restoreGraphicsState();
    }
  });

  return (pdf as jsPDFDocument).lastAutoTable.finalY ?? startY;
};
